import React, { useRef, useEffect, useCallback, useMemo } from "react";
import { Mail, Reply, Paperclip } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { LinkifiedText } from "@/components/LinkifiedText";
import { Separator } from "@/components/ui/separator";
import { AssetDownload } from "@/components/activityEvents/AssetDownload";
import type { RichActionData } from "./helpers/richActionHelpers";

interface NormalizedEmailData {
  to: string[];
  cc: string[];
  bcc: string[];
  from?: string;
  subject?: string;
  textBody?: string;
  htmlBody?: string;
  assetIds: string[];
  isDraft: boolean;
  isReply: boolean;
}

/**
 * Normalizes email arguments across the 4 different email action types
 * into a consistent shape for rendering.
 */
const normalizeEmailArgs = (
  args: Record<string, unknown>,
): NormalizedEmailData => {
  const toRaw = args.to ?? args.recipients;
  const to = Array.isArray(toRaw)
    ? toRaw.filter((v): v is string => typeof v === "string")
    : typeof toRaw === "string"
      ? [toRaw]
      : [];

  const cc = Array.isArray(args.cc)
    ? args.cc.filter((v): v is string => typeof v === "string")
    : [];

  const bcc = Array.isArray(args.bcc)
    ? args.bcc.filter((v): v is string => typeof v === "string")
    : [];

  const from =
    typeof args.from === "string"
      ? args.from
      : typeof args.forgepointMailAddress === "string"
        ? args.forgepointMailAddress
        : undefined;

  const subject = typeof args.subject === "string" ? args.subject : undefined;

  const contentType =
    typeof args.contentType === "string" ? args.contentType : undefined;

  const { textBody, htmlBody } = resolveBody(args, contentType);

  const assetIds = Array.isArray(args.assetIds)
    ? args.assetIds.filter((v): v is string => typeof v === "string")
    : [];

  const isDraft = args.draft === true;
  const isReply =
    typeof args.replyToMessageId === "string" ||
    typeof args.forwardMessageId === "string";

  return {
    to,
    cc,
    bcc,
    from,
    subject,
    textBody,
    htmlBody,
    assetIds,
    isDraft,
    isReply,
  };
};

/**
 * Resolves the text and HTML body from action arguments.
 * send_email_action uses plainTextBody + htmlBody.
 * Gmail uses body + contentType (text/plain | text/html).
 * SMTP/Outlook use content + contentType (text | html).
 */
const resolveBody = (
  args: Record<string, unknown>,
  contentType: string | undefined,
): { textBody?: string; htmlBody?: string } => {
  if (
    typeof args.plainTextBody === "string" ||
    typeof args.htmlBody === "string"
  ) {
    return {
      textBody:
        typeof args.plainTextBody === "string" ? args.plainTextBody : undefined,
      htmlBody: typeof args.htmlBody === "string" ? args.htmlBody : undefined,
    };
  }

  const bodyContent =
    typeof args.body === "string"
      ? args.body
      : typeof args.content === "string"
        ? args.content
        : undefined;

  if (!bodyContent) {
    return {};
  }

  const isHtml = contentType === "text/html" || contentType === "html";

  return isHtml ? { htmlBody: bodyContent } : { textBody: bodyContent };
};

interface EmailPreviewPanelProps {
  richActionData: RichActionData;
}

/**
 * Renders a formatted email preview in the rich action sidebar panel.
 * Normalizes input arguments from the 4 email action types and displays
 * header fields, body content, and attachments.
 */
export const EmailPreviewPanel: React.FC<EmailPreviewPanelProps> = ({
  richActionData,
}) => {
  const email = useMemo(
    () => normalizeEmailArgs(richActionData.actionArguments),
    [richActionData.actionArguments],
  );

  return (
    <div className="flex flex-col h-full">
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-muted-foreground" />
          <span className="font-semibold text-lg">
            {email.subject || "No Subject"}
          </span>
          {email.isDraft && <Badge variant="secondary">Draft</Badge>}
          {email.isReply && (
            <Badge variant="outline" className="gap-1">
              <Reply className="h-3 w-3" />
              Reply
            </Badge>
          )}
        </div>

        <div className="space-y-2 text-sm">
          {email.from && <EmailField label="From" value={email.from} />}
          {email.to.length > 0 && (
            <EmailField label="To" value={email.to.join(", ")} />
          )}
          {email.cc.length > 0 && (
            <EmailField label="CC" value={email.cc.join(", ")} />
          )}
          {email.bcc.length > 0 && (
            <EmailField label="BCC" value={email.bcc.join(", ")} />
          )}
        </div>
      </div>

      <Separator />

      <div className="flex-1 overflow-auto p-6">
        {email.htmlBody ? (
          <HtmlBodyRenderer html={email.htmlBody} />
        ) : email.textBody ? (
          <div className="whitespace-pre-wrap text-sm text-foreground break-words">
            <LinkifiedText text={email.textBody} />
          </div>
        ) : (
          <div className="text-sm text-muted-foreground italic">
            No email body
          </div>
        )}
      </div>

      {email.assetIds.length > 0 && (
        <>
          <Separator />
          <div className="p-6 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Paperclip className="h-4 w-4" />
              Attachments ({email.assetIds.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {email.assetIds.map((id) => (
                <AssetDownload key={id} assetId={id} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Renders a labeled email header field (From, To, CC, BCC).
 */
const EmailField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="flex gap-2">
    <span className="font-medium text-muted-foreground w-12 shrink-0">
      {label}:
    </span>
    <span className="text-foreground break-all">{value}</span>
  </div>
);

/**
 * Renders HTML email body in a sandboxed iframe that auto-sizes to content height,
 * re-measuring when the HTML loads and when the iframe width changes.
 * Prevents style bleed from the email HTML into the parent app.
 * Links open in a new tab via `<base target="_blank">` and sandbox popups.
 * Quoted-thread blockquote borders are recolored to the brand primary,
 * overriding the sender client's inline color.
 * @param props - The HTML body to render
 * @returns The sandboxed iframe
 */
export const HtmlBodyRenderer: React.FC<{ html: string }> = ({ html }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const primary = getComputedStyle(document.documentElement).getPropertyValue(
    "--primary",
  );
  const htmlWithBase = `<base target="_blank"><style>blockquote{border-left-color:hsl(${primary} / 0.6) !important}</style>${html}`;

  // Measures <html> rather than <body>: body.scrollHeight drops the margins of
  // its first/last children (e.g. <p>) that collapse through it, clipping text.
  const resizeIframe = useCallback(() => {
    const iframe = iframeRef.current;
    const root = iframe?.contentDocument?.documentElement;
    if (!iframe || !root) {
      return;
    }
    iframe.style.height = Math.ceil(root.getBoundingClientRect().height) + "px";
  }, []);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }
    const widthObserver = new ResizeObserver(resizeIframe);
    widthObserver.observe(iframe);
    return () => widthObserver.disconnect();
  }, [resizeIframe]);

  return (
    <iframe
      ref={iframeRef}
      srcDoc={htmlWithBase}
      onLoad={resizeIframe}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      className="w-full border-0"
      title="Email body"
    />
  );
};
